import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { useTheme, styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import Typography from '@mui/material/Typography';
import { JobCompany } from '../JobInterfaces';
import CircularProgress from '@mui/material/CircularProgress';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    if (React.isValidElement(dataSet)) {
        return dataSet;
    }

    if (dataSet.hasOwnProperty('group')) {
        return (
            <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        );
    }

    return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
            {dataSet[0] ? (
                <>
                    {dataSet[0].firma} <span className="jobAutocompleteLocation">{dataSet[0].ort}</span>
                </>
            ) : (
                (() => { console.log('dataSet[0] is undefined:', dataSet); return null; })()
            )}
        </Typography>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData: React.ReactNode[] = [];

    (children as React.ReactNode[]).forEach(
        (item: React.ReactNode & { children?: React.ReactNode[] }) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
        },
    );

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: React.ReactNode) => {
        if (child.hasOwnProperty('group')) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});

type AutocompleteProps = {
    options: JobCompany[],
    loading: boolean,
    open: boolean,
    setOpen: (open: boolean) => void,
    value: JobCompany,
    onChange: (newVal: JobCompany) => void
    onBlur: () => void,
    disabled: boolean
}
const theme = createTheme({
    palette: {
        primary: {
            main: "#10454F",
        },
    },
});

const Virtualize: React.FC<AutocompleteProps> = ({ options, loading, open, setOpen, value, onChange, onBlur, disabled }: AutocompleteProps) => {
    return (
        <ThemeProvider theme={theme}>
            <Autocomplete
                id="virtualizedAutocomplete"
                sx={{ marginTop: 1 }}
                disableListWrap
                PopperComponent={StyledPopper}
                ListboxComponent={ListboxComponent}
                options={options}
                renderInput={(params) => (
                    <div ref={params.InputProps.ref}>
                        <TextField {...params}
                            label="Firma*"
                            variant="standard"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>)}
                loadingText="Lädt..."
                /* renderOption={(props, option) => [props, option]} */
                renderOption={(props, option) => (
                    <li {...props}>
                        {option.firma} ({option.ort})
                    </li>
                )}
                loading={loading}
                open={open}
                onOpen={() => {
                    setOpen(true)
                }}
                onClose={() => {
                    setOpen(false)
                }}
                getOptionLabel={(option: JobCompany) => option.firma + " (" + option.ort +")" }
                value={value}
                onChange={(event: any, newValue: JobCompany | null) => {
                    if (newValue != null) {
                        onChange(newValue);
                    } else {
                        onChange(null);
                    }

                }}
                onBlur={onBlur}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                disabled={disabled}
            />
        </ThemeProvider>
    );
}

export default Virtualize;
